import { useState } from 'react'
import { cn } from '../lib/cn'
import { Select } from './Select'
import { DatePicker } from './DatePicker'

export function EditableText({
  value,
  onSave,
  placeholder = '—',
  className,
  width = 'w-40',
}: {
  value: string
  onSave: (v: string) => void
  placeholder?: string
  className?: string
  width?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false)
          onSave(draft)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setEditing(false)
            onSave(draft)
          }
          if (e.key === 'Escape') {
            setDraft(value)
            setEditing(false)
          }
        }}
        className={cn('rounded-md border border-brand-400 bg-shell px-2 py-1 text-sm text-shell-text-active outline-none ring-2 ring-brand-100', width)}
      />
    )
  }

  return (
    <button
      onClick={() => {
        setDraft(value)
        setEditing(true)
      }}
      className={cn(
        'truncate rounded-md px-2 py-1 text-left text-sm text-shell-text-active transition-colors hover:bg-shell-border/40',
        !value && 'text-shell-text/40',
        width,
        className,
      )}
      title={value}
    >
      {value || placeholder}
    </button>
  )
}

export function EditableDate({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  return (
    <DatePicker
      value={value}
      onChange={(e) => onSave(e.target.value)}
      placeholder="—"
      className="w-40 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-shell-text transition-colors hover:bg-shell-border/40"
    />
  )
}

export function EditableSelect<T extends string>({
  value,
  onSave,
  options,
  colors,
  width = 'w-32',
}: {
  value: T
  onSave: (v: T) => void
  options: T[]
  colors?: Record<string, { bg: string; text: string }>
  width?: string
}) {
  const style = colors?.[value]
  return (
    <Select
      value={value}
      onChange={(e) => onSave(e.target.value as T)}
      style={style ? { backgroundColor: style.bg, color: style.text } : undefined}
      className={cn(
        'h-full appearance-none rounded-md border border-transparent px-2 py-1.5 text-xs font-medium outline-none transition-colors hover:opacity-80 focus:border-brand-400 cursor-pointer',
        !style && 'bg-transparent text-shell-text hover:bg-shell-border/40 text-sm font-normal',
        width,
      )}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </Select>
  )
}
